/**
 * useCommandHandler Hook
 * Feature: 051-keybindings
 *
 * React hook for components to subscribe to commands.
 * Automatically manages subscription lifecycle (mount/unmount).
 */

import { useEffect } from 'react';
import { useCommandBus } from './CommandBus';
import type { CommandId } from './commandDefinitions';

/**
 * Hook for components to handle commands
 *
 * @param commandId - Type-safe command ID (autocomplete supported)
 * @param handler - Function to execute when command is triggered
 * @param isEnabled - Whether this handler is currently active (default: true)
 *
 * @example
 * useCommandHandler(
 *   'critique.nextPhoto',
 *   () => selectPhoto(nextPhotoId),
 *   photos.length > 0 && !isEditing
 * );
 */
export function useCommandHandler(
  commandId: CommandId,
  handler: () => void | Promise<void>,
  isEnabled: boolean = true
): void {
  const bus = useCommandBus();

  useEffect(() => {
    // Don't subscribe if disabled
    if (!isEnabled) return;

    // Subscribe to command
    const unsubscribe = bus.subscribe(commandId, handler);

    // Cleanup on unmount or when dependencies change
    return unsubscribe;

    // Note: handler should be wrapped in useCallback by caller to avoid unnecessary re-subscriptions
  }, [bus, commandId, handler, isEnabled]);
}
